import {AxiosInstance} from "axios";
import {Tag} from "@/tag/interfaces/Tag";
import {ProgramBookable} from "@/program/interfaces/ProgramBookable";
import {ProductRecommendation} from "@/core/interfaces/Product";

export interface SupportProgramIndexResource {
    id: number;
    name: string;
    tags: Tag[];
    bookers: ProgramBookable[] | null;
}

export interface SupportProgramIndexRequest {
    bookable?: boolean;
}

export interface ProductRecommendationPayload {
    product_id: number;
    title?: string | null;
    description?: string | null;
    posology?: string | null;
    comments?: string | null;
}

export default function (client: AxiosInstance) {
    return {

        /**
         * Get the support program index.
         */
        async index(params: SupportProgramIndexRequest = {}) {
            return client.get('support-programs', {params})
                .then(response => response.data.data);
        },

        /**
         * Get the support program flyer.
         */
        async flyer(supportProgramId: number, consumerKey: string) {
            const params = {consumer_key: consumerKey};
            return client.get(`support-programs/${supportProgramId}/flyer`, {
                params,
                responseType: 'blob'
            })
                .then(response => response.data);
        },

        /**
         * Get the data an addon exposes for a consumer, e.g. the pet tracking of the `pets` addon.
         *
         * @param supportProgramId The support program id.
         * @param consumerKey The consumer key.
         * @param slug The addon slug.
         */
        async consumerAddon<T>(supportProgramId: number, consumerKey: string, slug: string): Promise<T> {
            return client.get(`support-programs/${supportProgramId}/consumers/${consumerKey}/addons/${slug}`)
                .then(response => response.data.data);
        },

        /**
         * Get the support program product recommendations.
         */
        async productRecommendations(supportProgramId: number | string): Promise<ProductRecommendation[]> {
            return client.get(`support-programs/${supportProgramId}/product-recommendations`)
                .then(response => response.data.data);
        },

        /**
         * Add a product recommendation to the support program.
         */
        async addProductRecommendation(
            supportProgramId: number | string,
            payload: ProductRecommendationPayload
        ): Promise<ProductRecommendation> {
            return client.post(`support-programs/${supportProgramId}/product-recommendations`, payload)
                .then(response => response.data.data);
        },

        /**
         * Delete a product recommendation from the support program.
         */
        async deleteProductRecommendation(
            supportProgramId: number | string,
            recommendationId: number
        ): Promise<void> {
            return client.delete(`support-programs/${supportProgramId}/product-recommendations/${recommendationId}`)
                .then(() => undefined);
        }
    };
}
